/* eslint-disable no-unused-vars */
import { useMemo, useState } from "react";
import Select, {
  MenuProps,
  components as reactSelectComponents,
} from "react-select";
import { useTheme } from "next-themes";
import { reactSelectNativeStyle } from "@/components/UI/reactSelect";
import { formatNumberWithCommas } from "@/lib/utilities";

// LQC-2254 - type-to-find over a reference-data store.
//
// The options are computed from the typed term rather than handed to react-select up
// front: the bond universe is far too large to render as a full option list, so the
// caller returns an already-ranked, already-capped slice and filterOption is switched
// off so react-select does not filter the ranking away again.
export interface IReferenceDataOption {
  label: string;
  value: string;
}

// The capped options plus how many matched before capping.
export interface IReferenceDataOptions {
  options: IReferenceDataOption[];
  totalMatches: number;
}

// How many options a dropdown shows at a time, and how many more each "Show more" adds.
export const REFERENCE_DATA_PAGE_SIZE = 50;

interface IReferenceDataSelect {
  // The current selection. label is what shows; value is the underlying code/ISIN.
  selected: IReferenceDataOption | null;
  placeholder: string;
  // Ranked results for the typed term, capped at limit. Memoise in the caller.
  searchOptions: (term: string, limit: number) => IReferenceDataOptions;
  onSelect: (option: IReferenceDataOption | null) => void;
  testId: string;
  isDisabled?: boolean;
}

export interface IOptionCountSummary {
  hasMore: boolean;
  countLabel: string;
  moreLabel: string;
}

// What the menu footer says. hasMore false means the list is complete and the footer is
// not rendered at all.
export function returnOptionCountSummary(
  shownCount: number,
  totalMatches: number,
  pageSize: number = REFERENCE_DATA_PAGE_SIZE,
): IOptionCountSummary {
  const remaining = Math.max(0, totalMatches - shownCount);
  return {
    hasMore: remaining > 0,
    countLabel: `${formatNumberWithCommas(shownCount)} of ${formatNumberWithCommas(totalMatches)}`,
    moreLabel: `Show ${Math.min(pageSize, remaining)} more`,
  };
}

// The count and its Show more button are pinned to the foot of the OPEN MENU, for two
// reasons. Rendered under the control instead they would (a) change the ticket row's
// height every time the menu opened, shoving every row below it down, and (b) be
// invisible anyway - the portalled menu drops down over exactly that spot. Inside Menu
// rather than MenuList so the footer sits below the scroll area and stays put while the
// options scroll.
//
// The menu's own container is unstyled by reactSelectNativeStyle (only menuList is), so
// this carries the background itself.
const MenuWithCount = (props: MenuProps<IReferenceDataOption, false>) => {
  const { optionCount, onShowMore, countTestId } = props.selectProps as any;
  return (
    <reactSelectComponents.Menu {...props}>
      {props.children}
      {optionCount?.hasMore && (
        <div
          data-testid={countTestId}
          id={countTestId}
          className="flex items-center justify-between gap-2 rounded-b border-t border-gray-300 bg-white px-2 py-1 text-[11px] text-gray-700 dark:border-gray-600 dark:bg-[#222222] dark:text-gray-200"
        >
          <span>{optionCount.countLabel}</span>
          <button
            type="button"
            data-testid={`${countTestId}ShowMore`}
            id={`${countTestId}ShowMore`}
            className="font-semibold text-sky-600 hover:underline dark:text-sky-400"
            // react-select closes the menu when the input loses focus, so the click must
            // not move focus. preventDefault on mousedown keeps the caret in the input.
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={onShowMore}
          >
            {optionCount.moreLabel}
          </button>
        </div>
      )}
    </reactSelectComponents.Menu>
  );
};

// Module scope so react-select is not handed a new component map on every render.
const SELECT_COMPONENTS = { Menu: MenuWithCount };

const ReferenceDataSelect = ({
  selected,
  placeholder,
  searchOptions,
  onSelect,
  testId,
  isDisabled = false,
}: IReferenceDataSelect) => {
  const { theme } = useTheme();
  const [inputValue, setInputValue] = useState("");
  const [visibleCount, setVisibleCount] = useState(REFERENCE_DATA_PAGE_SIZE);

  const { options, totalMatches } = useMemo(
    () => searchOptions(inputValue, visibleCount),
    [searchOptions, inputValue, visibleCount],
  );

  const optionCount = returnOptionCountSummary(options.length, totalMatches);

  // Typing is a new search, so it starts from the first page again.
  const resetPaging = () => setVisibleCount(REFERENCE_DATA_PAGE_SIZE);

  return (
    // Fixed height so opening the menu can never resize the ticket row.
    <div data-testid={testId} id={testId} className="h-[28px] w-full min-w-0">
      <Select
        className="basic-single"
        classNamePrefix="select"
        placeholder={placeholder}
        value={selected}
        options={options}
        inputValue={inputValue}
        // Already ranked upstream - re-filtering here would undo the ordering.
        filterOption={null}
        onInputChange={(next, meta) => {
          if (meta.action === "input-change") {
            setInputValue(next);
            resetPaging();
          } else if (meta.action === "menu-close") {
            setInputValue("");
            resetPaging();
          }
        }}
        onChange={(option) => onSelect(option as IReferenceDataOption | null)}
        noOptionsMessage={() => "No match"}
        isDisabled={isDisabled}
        isClearable
        isSearchable
        styles={reactSelectNativeStyle(theme)}
        components={SELECT_COMPONENTS}
        maxMenuHeight={200}
        menuPlacement="auto"
        menuPosition="fixed"
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : undefined
        }
        // Extra props ride along on selectProps for MenuWithCount to read. react-select
        // has no typed slot for custom props short of module augmentation.
        {...({
          optionCount,
          countTestId: `${testId}Count`,
          onShowMore: () =>
            setVisibleCount((count) => count + REFERENCE_DATA_PAGE_SIZE),
        } as any)}
      />
    </div>
  );
};

export default ReferenceDataSelect;
